import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Icon } from "@dialogueconsulting/sked-ui";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { FIELDS } from "constants/PostFormFields";
import { Spinner } from "@/components/ui/spinner";
import { PlatformLabelBadges } from "../PlatformLabelBadges/PlatformLabelBadges";
import { toast } from "react-toastify";
import { MediaModal } from "features/media-library";
import { useOpenableState } from "libs/use-openable-state";
import { VideoCoverSelectorProps } from "./types";
import { isSafariLikeUserAgent, resolveToastPlatformName } from "./utils";
import { Alert } from "ui";
import { AlertType } from "ui/alert";
import { VideoFrameSelector } from "./FrameSelector";
import { skedApi } from "api/_skedApi";
import { Button } from "@/components/ui/button";
import { useVideoCoverState } from "./hooks/useVideoCoverState";
import { useVideoCoverOperations } from "./hooks/useVideoCoverOperations";
import { usePlatformPermissions } from "./hooks/usePlatformPermissions";
import { isImagePreviewUrl } from "utils/media";
import {
  VIDEO_COVER_TIMESTAMPS,
  VIDEO_COVER_TYPES,
} from "constants/VideoCoverConstants";

export const VideoCoverSelector = ({
  videoUrl,
  originalMediaUrl,
  lowQualityMediaUrl,
  isLoading: isLoadingVideo,
  videoMedia,
  thumbnails,
  updateFields,
  videoCoverImageTimestamp,
  videoCoverPreview = "",
  videoCoverType,
  videoCoverOriginalTimestamp,
  videoCoverOriginalType,
  videoCoverOriginal,
  originalCover = "",
  thumbnailUrl,
  selectedPlatform,
  platforms,
  disabled,
  videoLoadingError,
  onCoverPreviewChange,
  coverFrames,
  lazyBlobRef,
  videoDurationFromMeta,
}: VideoCoverSelectorProps) => {
  // Initialize hooks
  const {
    state: {
      cover,
      isLoadingThumb,
      isEditing,
      showLowResFallbackWarning,
      originalTimestamp,
      originalCoverType,
    },
    setCover,
    setIsLoadingThumb,
    setIsEditing,
    setShowLowResFallbackWarning,
    setOriginalValues,
  } = useVideoCoverState(videoCoverPreview);
  const effectiveCoverFrames = coverFrames;
  const [failedDisplayCover, setFailedDisplayCover] = useState("");

  const coverFramesKey = useMemo(
    () =>
      effectiveCoverFrames
        ?.map((frame) => `${frame.timestampMs}:${frame.url}`)
        .join("|") ?? "",
    [effectiveCoverFrames]
  );

  const mediaIdentity = originalMediaUrl || lowQualityMediaUrl || videoUrl;
  const coverSessionKey = [
    selectedPlatform,
    mediaIdentity,
    coverFramesKey,
  ].join("|");
  const previousCoverSessionKeyRef = useRef<string>("");
  const hasInitializedOriginalCover = useRef(false);

  // Initialize original values only once on mount
  const hasInitializedOriginalValues = useRef(false);

  useEffect(() => {
    if (previousCoverSessionKeyRef.current === coverSessionKey) {
      return;
    }

    previousCoverSessionKeyRef.current = coverSessionKey;
    hasInitializedOriginalValues.current = false;
    hasInitializedOriginalCover.current = false;
    hasGeneratedCoverRef.current = false;
    setCover(videoCoverPreview);
    setFailedDisplayCover("");
    setIsEditing(false);
    setShowLowResFallbackWarning(false);
  }, [
    coverSessionKey,
    setCover,
    videoCoverPreview,
    setIsEditing,
    setShowLowResFallbackWarning,
  ]);

  useEffect(() => {
    if (hasInitializedOriginalCover.current) {
      return;
    }

    if (videoCoverOriginal) {
      hasInitializedOriginalCover.current = true;
      return;
    }

    if (!originalCover) {
      return;
    }

    updateFields(
      {
        [FIELDS.VIDEO_COVER_ORIGINAL]: originalCover,
      },
      selectedPlatform
    );
    hasInitializedOriginalCover.current = true;
  }, [originalCover, selectedPlatform, updateFields, videoCoverOriginal]);

  useEffect(() => {
    if (!hasInitializedOriginalValues.current) {
      const timestamp =
        videoCoverOriginalTimestamp ??
        videoCoverImageTimestamp ??
        VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME;
      const coverType =
        videoCoverOriginalType ??
        videoCoverType ??
        (timestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER
          ? VIDEO_COVER_TYPES.UPLOADED
          : timestamp > VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME
            ? VIDEO_COVER_TYPES.FRAME_SELECTED
            : VIDEO_COVER_TYPES.PLATFORM_DEFAULT);

      setOriginalValues({
        timestamp,
        coverType,
      });
      hasInitializedOriginalValues.current = true;

      if (
        videoCoverOriginalTimestamp === undefined ||
        videoCoverOriginalType === undefined
      ) {
        updateFields(
          {
            [FIELDS.VIDEO_COVER_ORIGINAL_TIMESTAMP]: timestamp,
            [FIELDS.VIDEO_COVER_ORIGINAL_TYPE]: coverType,
          },
          selectedPlatform
        );
      }
    }
  }, [
    videoCoverImageTimestamp,
    videoCoverType,
    videoCoverOriginalTimestamp,
    videoCoverOriginalType,
    setOriginalValues,
    updateFields,
    selectedPlatform,
  ]);

  const { allowUpload, displayPlatforms, platformFullname } =
    usePlatformPermissions({
      selectedPlatform,
      platforms,
      videoLoadingError,
      videoUrl,
    });
  const uploadToastPlatformName = useMemo(
    () =>
      selectedPlatform === "LINKED"
        ? formatPlatformNames(
            displayPlatforms.map((platform) => platform.LABEL)
          )
        : resolveToastPlatformName(selectedPlatform),
    [displayPlatforms, selectedPlatform]
  );
  const coverUpdatedMessage =
    selectedPlatform === "LINKED"
      ? "Video cover updated."
      : `${platformFullname} cover updated.`;

  const {
    onCoverChanged,
    onCoverUploaded,
    onRevertToOriginal,
    generateOriginalCover,
  } = useVideoCoverOperations({
    updateFields,
    platforms,
    originalMediaUrl,
    lowQualityMediaUrl,
    videoUrl,
    thumbnailUrl,
    videoCoverImageTimestamp,
    videoCoverPreview,
    setCover,
    setIsLoadingThumb,
    setShowLowResFallbackWarning,
    setOriginalValues,
    platformFullname,
    coverFrames: effectiveCoverFrames,
    lazyBlobRef,
    coverSessionKey,
    selectedPlatform,
  });

  const isLoading = isLoadingVideo || isLoadingThumb;

  // Guard to prevent generateOriginalCover from being called multiple times
  // during the same cover-panel session (avoids re-render loop from Redux updates)
  const hasGeneratedCoverRef = useRef(false);

  // Refs for values used in cleanup path only (should not retrigger effect)
  const coverRef = useRef(cover);
  coverRef.current = cover;
  const videoCoverPreviewRef = useRef(videoCoverPreview);
  videoCoverPreviewRef.current = videoCoverPreview;

  useEffect(() => {
    if (isLoadingVideo) {
      return;
    }

    if (!videoUrl) {
      hasGeneratedCoverRef.current = false;
      // Only clear if we don't have a persisted cover - don't wipe saved data during loading
      if (
        !videoCoverPreviewRef.current &&
        (originalCover || coverRef.current)
      ) {
        setCover("");
        setShowLowResFallbackWarning(false);
        updateFields(
          {
            [FIELDS.VIDEO_COVER_ORIGINAL]: "",
            [FIELDS.VIDEO_COVER_PREVIEW]: "",
            [FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP]: 0,
            [FIELDS.VIDEO_COVER_TYPE]: "platform_default",
            [FIELDS.VIDEO_COVER_PREVIEW_SOURCE]: undefined,
          },
          selectedPlatform
        );
      }
      return;
    }

    if (!originalCover && !hasGeneratedCoverRef.current) {
      hasGeneratedCoverRef.current = true;
      generateOriginalCover();
    }
  }, [
    originalCover,
    videoUrl,
    isLoadingVideo,
    generateOriginalCover,
    setCover,
    setShowLowResFallbackWarning,
    updateFields,
    selectedPlatform,
  ]);

  useEffect(() => {
    onCoverPreviewChange?.(cover || originalCover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cover, originalCover]);

  // All business logic is now handled by the hooks

  return (
    <Wrap>
      {isSafariLikeUserAgent() && (
        <Alert
          type={AlertType.warning}
          icon={true}
          isExpandable={true}
          title="Safari Compatibility Issue"
          defaultExpanded={false}
        >
          Looks like you are using Safari. The cover selection might not work
          properly due to technical limitations. Please use a different browser
          e.g. Google Chrome or Firefox. Contact support if you have any
          questions.
        </Alert>
      )}
      <Head>
        <Title>Cover</Title>
        {!isEditing && (
          <PlatformLabelBadges
            className="ml-2"
            selectedPlatform={selectedPlatform}
            platforms={displayPlatforms.map((platform) => platform.NAME)}
          />
        )}
        {isEditing && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div style={{ position: "relative" }}>
                <Icon name="alert-circle" color="#333333" size="sm" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-[450px]">
              Drag or click on a thumbnail and click &apos;done&apos; to
              finalize your selection.
            </TooltipContent>
          </Tooltip>
        )}
      </Head>

      {isEditing && (
        <VideoFrameSelector
          videoUrl={videoUrl}
          thumbnails={thumbnails}
          media={videoMedia}
          isLoading={isLoading}
          onClose={() => setIsEditing(false)}
          onChange={(cover: string, timestamp: number) =>
            onCoverChanged(cover, timestamp, coverUpdatedMessage)
          }
          coverFrames={effectiveCoverFrames}
          videoDurationFromMeta={videoDurationFromMeta}
          lazyBlobRef={lazyBlobRef}
        />
      )}

      {!isEditing && (
        <>
          <Content>
            {isLoading ? (
              <Cover>
                <Spinner size="lg" data-testid="loading-spinner" />
              </Cover>
            ) : (
              <Cover data-testid="video-cover">
                {(() => {
                  const displayCover = [cover, originalCover].find(
                    (candidate) =>
                      isImagePreviewUrl(candidate) &&
                      candidate !== failedDisplayCover
                  );
                  return displayCover ? (
                    <img
                      src={displayCover}
                      alt="video-cover"
                      onError={() => setFailedDisplayCover(displayCover)}
                    />
                  ) : (
                    <Icon name="image-01" color="#333333" size="sm" />
                  );
                })()}
              </Cover>
            )}
            {!disabled && (
              <Actions>
                {((videoMedia && thumbnails.length > 0) ||
                  effectiveCoverFrames?.length) && (
                  <Button
                    variant="blue-secondary"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    Select cover
                  </Button>
                )}
                {allowUpload && (
                  <VideoCoverUploadButton
                    onUpload={onCoverUploaded}
                    platformName={uploadToastPlatformName}
                  />
                )}
                {cover && cover !== originalCover && (
                  <Button
                    variant="blue-tertiary"
                    size="sm"
                    onClick={() => {
                      onRevertToOriginal(
                        originalCover,
                        originalTimestamp ?? videoCoverImageTimestamp,
                        originalCoverType ?? "platform_default",
                        originalCover
                      );
                    }}
                  >
                    Revert to original
                  </Button>
                )}
              </Actions>
            )}
          </Content>
          {showLowResFallbackWarning && (
            <InlineAlert
              type={AlertType.warning}
              icon={true}
              title="Cover may be lower quality"
            >
              We couldn&apos;t load the original video, so this cover may save
              at a lower quality.
            </InlineAlert>
          )}
        </>
      )}
    </Wrap>
  );
};

function formatPlatformNames(platformNames: string[]) {
  const names = platformNames.filter(Boolean);

  if (names.length === 0) {
    return "video";
  }

  if (names.length <= 2) {
    return names.join(" and ");
  }

  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

function VideoCoverUploadButton({
  onUpload,
  platformName,
}: {
  onUpload: (url: string, thumbnailUrl: string) => void;
  platformName: string;
}) {
  const MediaLibraryModal = useOpenableState();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelect = async ([media]: any[]) => {
    if (media.type !== "image") {
      toast.error("Selected media is not an image.");
      return;
    }

    if (media?.meta?.width < 1080) {
      toast.warn(
        "Selected image has width less than Instagram's recommended size, they may be pixelated."
      );
    }
    if (media?.url.endsWith(".heic") || media?.url.endsWith(".heif")) {
      // just for video previews we don't support HEIC
      const conversionResult = await skedApi.get<{
        url: string;
        assetId: string;
      }>(`/content-inbox/img-url/${media.asset_id}/jpg`);
      media.url = conversionResult.data.url;
    }

    onUpload(media?.url, media?.thumbnailUrl || media?.url);

    toast.success(`The ${platformName} cover has been successfully updated.`);
    MediaLibraryModal.close();
  };

  return (
    <>
      <MediaModal
        show={MediaLibraryModal.isOpened}
        onHide={MediaLibraryModal.close}
        limit={1}
        onSelect={onSelect}
        selectedAccountIds={[]}
      />
      <Button
        variant="blue-tertiary"
        size="sm"
        onClick={MediaLibraryModal.open}
      >
        Upload cover
      </Button>
    </>
  );
}

const Wrap = styled.div`
  margin: 16px 0;
}
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

const Title = styled.div`
  color: var(--Text-T01, #333);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  margin-right: 8px;
`;
const Content = styled.div`
  display: flex;
  align-items: center;
`;

const InlineAlert = styled(Alert)`
  margin-top: 8px;
`;

const Cover = styled.div`
  width: 64px;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--Neutral-N200, #c4c4c4);
  background: var(--Neutral-N50, #f2f2f2);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Actions = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
